import { QueryClient, SetDataOptions, Updater } from "@tanstack/react-query";
import { SessionMethodName, SessionMethodParams, SessionQueryDataForMethod, SessionQueryKey } from "../../types";
import { buildSessionQueryKey } from "../../keys";

/**
 * Arguments for setting session query data in cache.
 *
 * @template Method - The session method name
 */
export interface SetSessionQueryDataArgs<Method extends SessionMethodName> {
  /** The session method name */
  method: Method;
  /** The request parameters for the session method */
  request: Partial<SessionMethodParams<Method>>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The data to set, or an updater function */
  updater: Updater<SessionQueryDataForMethod<Method> | undefined, SessionQueryDataForMethod<Method> | undefined>;
  /** Optional set data options (e.g., updatedAt) */
  options?: SetDataOptions;
}

/**
 * Return type for setting session query data in cache.
 *
 * @template Method - The session method name
 */
export interface SetSessionQueryDataReturn<Method extends SessionMethodName> {
  /** The query that was updated with its original data before the update */
  originalQuery: readonly [SessionQueryKey<Method>, SessionQueryDataForMethod<Method> | undefined, number | undefined];
  /** The query that was updated with its new data after the update */
  updatedQuery: readonly [SessionQueryKey<Method>, SessionQueryDataForMethod<Method> | undefined];
}

/**
 * Sets query data in the query client cache for a session query.
 * Can be used to update the cache directly without making a network request.
 *
 * **Special Updater Return Values:**
 * - Return `undefined`: Skip the update (no change to cache)
 * - Return `null`: Reset the query (remove from cache) - useful for deletions
 * - Return data: Update the cache with the new data
 *
 * @param args - The set session query data arguments
 * @returns An object containing the original query data before update and the updated query data
 *
 * @example
 * ```tsx
 * // Set data directly
 * const { originalQuery, updatedQuery } = setSessionQueryData({
 *   queryClient,
 *   method: "loadEndusers",
 *   request: { filter: { status: "active" } },
 *   updater: { endusers: [...], total: 10 },
 * });
 * // originalQuery: [queryKey, oldData, timestamp]
 * // updatedQuery: [queryKey, newData]
 *
 * // Update existing data with an updater function
 * const result = setSessionQueryData({
 *   queryClient,
 *   method: "loadEndusers",
 *   request: { filter: { status: "active" } },
 *   updater: (old) => old ? { ...old, endusers: [...old.endusers, newUser] } : undefined,
 * });
 *
 * // Reset/delete query data (useful for cache invalidation)
 * setSessionQueryData({
 *   queryClient,
 *   method: "loadEndusers",
 *   request: { filter: { status: "deleted" } },
 *   updater: () => null, // Resets the query
 * });
 *
 * // Set data with custom options
 * setSessionQueryData({
 *   queryClient,
 *   method: "loadEndusers",
 *   request: { filter: { status: "active" } },
 *   updater: { endusers: [...], total: 10 },
 *   options: {
 *     updatedAt: Date.now(), // Set custom update timestamp
 *   },
 * });
 * ```
 */
export const setSessionQueryData = <Method extends SessionMethodName>({
  method,
  request,
  queryClient,
  updater,
  options,
}: SetSessionQueryDataArgs<Method>): SetSessionQueryDataReturn<Method> => {
  const queryKey = buildSessionQueryKey({ method, request });

  let originalData: SessionQueryDataForMethod<Method> | undefined;
  let shouldReset = false;

  const wrappedUpdater: Updater<
    SessionQueryDataForMethod<Method> | undefined,
    SessionQueryDataForMethod<Method> | undefined
  > = (old) => {
    originalData = old;
    // @ts-expect-error - TypeScript can't narrow union types when QueryData could itself be a function
    const updatedData = typeof updater === "function" ? updater(old) : updater;

    if (updatedData === null) {
      shouldReset = true;
    }

    return updatedData;
  };

  const originalUpdatedAt = queryClient.getQueryState(queryKey)?.dataUpdatedAt;

  // First, call the updater to determine if we should reset and capture original data
  const currentData = queryClient.getQueryData<SessionQueryDataForMethod<Method>>(queryKey);
  wrappedUpdater(currentData);

  let updatedData: SessionQueryDataForMethod<Method> | undefined;

  if (shouldReset) {
    // Reset the query when updater returns null
    queryClient.resetQueries({ queryKey, exact: true });
    updatedData = undefined;
  } else {
    // Otherwise, apply the update normally
    updatedData = queryClient.setQueryData<SessionQueryDataForMethod<Method>>(queryKey, wrappedUpdater, options);
  }

  return {
    originalQuery: [queryKey, originalData, originalUpdatedAt],
    updatedQuery: [queryKey, updatedData],
  };
};
